import { Button, Checkbox, Divider, Dropdown, FormLabel, Group, IconSliders, Wrapper } from '@screentone/core';

import ContentTypeToken from 'components/content-type-token/ContentTypeToken';
import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
import { PublicationSettingSearchableContentType } from 'data/generated/graphql';
import { getContentLabel } from 'utils/contentType';
import styles from './ContentTypeFilter.module.scss';

export type ContentTypeFilterType = PublicationSettingSearchableContentType;

interface ContentTypeFilterProps {
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  handleDeselectAll: () => void;
  handleSelectAll: () => void;
  handleSelectArticleOnly: () => void;
  isActive: boolean;
  isLoading: boolean;
  publishStatusValues: ContentTypeFilterType[];
  values: ContentTypeFilterType[];
  currentProperty: string | null;
  labelOverrides?: Record<string, string>;
  iconSvgOverrides?: Record<string, { path: string }>;
  categoryOverrides?: Record<string, 'type' | 'brand'>;
}

export const ContentTypeFilter = ({
  isActive,
  isLoading,
  publishStatusValues,
  handleChange,
  handleDeselectAll,
  handleSelectAll,
  handleSelectArticleOnly,
  values,
  currentProperty,
  labelOverrides,
  iconSvgOverrides,
  categoryOverrides
}: ContentTypeFilterProps) => {
  // Group content types by category, preferring explicit categoryOverrides over the hardcoded lookup
  const groupedContentTypes = publishStatusValues.reduce(
    (acc, publishStatusValue) => {
      const category = categoryOverrides?.[publishStatusValue] ?? getContentLabel(publishStatusValue).category;
      if (category === 'type') {
        acc.types.push(publishStatusValue);
      } else {
        acc.brands.push(publishStatusValue);
      }
      return acc;
    },
    { types: [] as ContentTypeFilterType[], brands: [] as ContentTypeFilterType[] }
  );

  const hasNoContentTypes = publishStatusValues.length === 0;
  const selectedCount = publishStatusValues.filter((contentType) => values.includes(contentType)).length;
  const isSelectAllDisabled = selectedCount === publishStatusValues.length;
  const isDeselectAllDisabled = selectedCount === 0;
  const showArticleOnlyButton = publishStatusValues.includes(PublicationSettingSearchableContentType.Article);
  const isArticleOnlyDisabled = values.length === 1 && values[0] === PublicationSettingSearchableContentType.Article;

  const showLabels = groupedContentTypes.types.length > 0 && groupedContentTypes.brands.length > 0;

  // Determine the trigger button text based on what content is available
  const getTriggerText = () => {
    const hasTypes = groupedContentTypes.types.length > 0;
    const hasBrands = groupedContentTypes.brands.length > 0;

    if (hasTypes && hasBrands) {
      return 'Type & Brand';
    }
    if (hasTypes) {
      return 'Type';
    }
    if (hasBrands) {
      return 'Brand';
    }
    return 'Type & Brand'; // Fallback
  };

  return (
    <Dropdown
      data-testid="content-type-filter-button"
      trigger={
        <Group
          data-testid="content-type-filter-button-label"
          gap="xs"
          margin={{ right: 'sm' }}
          className={isActive ? styles.active : ''}
        >
          <IconSliders data-testid="content-type-filter-button-icon" /> {getTriggerText()}
        </Group>
      }
      disabled={isLoading}
      padding={{ all: 'none' }}
      zIndex="top"
    >
      {hasNoContentTypes ? (
        <Wrapper padding={{ all: 'sm' }}>
          <NoSearchResults title="Content Types" bodyText="No content types are available for this publication." />
        </Wrapper>
      ) : (
        <>
          <Wrapper padding={{ all: 'md' }}>
            {groupedContentTypes.types.length > 0 && (
              <>
                {showLabels && <FormLabel label="Content Type" margin={{ bottom: 'sm' }} />}
                {groupedContentTypes.types.map((publishStatusValue) => (
                  <FormLabel
                    data-testid="content-type-filter-item"
                    className={styles.dropDown}
                    key={publishStatusValue}
                    label={
                      <div data-testid="content-type-filter-label" className={styles.label}>
                        <ContentTypeToken
                          data-testid="content-type-filter-label-icon"
                          type={publishStatusValue}
                          margin={{ right: 'sm' }}
                          currentProperty={currentProperty}
                          iconSvgPath={iconSvgOverrides?.[publishStatusValue]?.path}
                        />
                        {labelOverrides?.[publishStatusValue] ?? getContentLabel(publishStatusValue).name}
                      </div>
                    }
                    labelPosition="right"
                    wrap={false}
                    fullWidth
                  >
                    <Checkbox
                      data-testid="content-type-filter-item-checkbox"
                      name="contentTypes"
                      value={publishStatusValue}
                      checked={values.includes(publishStatusValue)}
                      onChange={handleChange}
                      disabled={values.length === 1 && values.includes(publishStatusValue)}
                    />
                  </FormLabel>
                ))}
              </>
            )}
            {groupedContentTypes.brands.length > 0 && (
              <>
                {showLabels && <FormLabel label="Brand (all content types)" margin={{ top: 'md', bottom: 'sm' }} />}
                {groupedContentTypes.brands.map((publishStatusValue) => (
                  <FormLabel
                    data-testid="content-type-filter-item"
                    className={styles.dropDown}
                    key={publishStatusValue}
                    label={
                      <div data-testid="content-type-filter-label" className={styles.label}>
                        <ContentTypeToken
                          data-testid="content-type-filter-label-icon"
                          type={publishStatusValue}
                          margin={{ right: 'sm' }}
                          currentProperty={currentProperty}
                          iconSvgPath={iconSvgOverrides?.[publishStatusValue]?.path}
                        />
                        {labelOverrides?.[publishStatusValue] ?? getContentLabel(publishStatusValue).name}
                      </div>
                    }
                    labelPosition="right"
                    wrap={false}
                    fullWidth
                  >
                    <Checkbox
                      data-testid="content-type-filter-item-checkbox"
                      name="contentTypes"
                      value={publishStatusValue}
                      checked={values.includes(publishStatusValue)}
                      onChange={handleChange}
                      disabled={values.length === 1 && values.includes(publishStatusValue)}
                    />
                  </FormLabel>
                ))}
              </>
            )}
          </Wrapper>
          <Divider />
          <Wrapper padding={{ all: 'md' }}>
            <Button
              tertiary
              onClick={handleSelectAll}
              disabled={isSelectAllDisabled}
              data-testid="select-all-button"
              fullWidth
            >
              Select all
            </Button>
            <Button
              tertiary
              onClick={handleDeselectAll}
              disabled={isDeselectAllDisabled}
              data-testid="deselect-all-button"
              fullWidth
            >
              Deselect all
            </Button>
            {showArticleOnlyButton && (
              <Button
                tertiary
                onClick={handleSelectArticleOnly}
                disabled={isArticleOnlyDisabled}
                data-testid="select-article-only-button"
                fullWidth
              >
                Select article only
              </Button>
            )}
          </Wrapper>
        </>
      )}
    </Dropdown>
  );
};
